import { Schema, MarkSpec, NodeSpec } from 'prosemirror-model';
import { createFileLogger } from '../../../utils/logger';

const log = createFileLogger('SCHEMA_EXTENSION_REGISTRY');

/**
 * Registry for plugins to register custom marks and nodes
 * This allows plugins to extend the ProseMirror schema without modifying core schema.ts
 */
class SchemaExtensionRegistry {
  private customMarks: Map<string, MarkSpec> = new Map();
  private customNodes: Map<string, NodeSpec> = new Map();
  private isLocked: boolean = false;
  
  /**
   * Register a custom mark from a plugin
   */
  registerMark(name: string, spec: MarkSpec): void {
    if (this.isLocked) {
      throw new Error(`[SchemaExtension] Cannot register mark '${name}' after schema is built`);
    }
    
    if (this.customMarks.has(name)) {
      log.warn(`[SchemaExtension] Mark '${name}' already registered, overwriting`);
    }
    
    this.customMarks.set(name, spec);
    log.info(`[SchemaExtension] Registered mark: ${name}`);
  }
  
  /**
   * Register a custom node from a plugin
   */
  registerNode(name: string, spec: NodeSpec): void {
    if (this.isLocked) {
      throw new Error(`[SchemaExtension] Cannot register node '${name}' after schema is built`);
    }
    
    if (this.customNodes.has(name)) {
      log.warn(`[SchemaExtension] Node '${name}' already registered, overwriting`);
    }
    
    this.customNodes.set(name, spec);
    log.info(`[SchemaExtension] Registered node: ${name}`);
  }
  
  /**
   * Build extended schema with all registered marks and nodes
   */
  buildExtendedSchema(baseSchema: Schema): Schema {
    this.isLocked = true;
    
    // Get base marks and nodes
    const baseMarks = baseSchema.spec.marks;
    const baseNodes = baseSchema.spec.nodes;
    
    // Add custom marks
    let extendedMarks = baseMarks;
    for (const [name, spec] of this.customMarks) {
      extendedMarks = extendedMarks.addToEnd(name, spec);
      log.info(`[SchemaExtension] Added mark to schema: ${name}`);
    }
    
    // Add custom nodes
    let extendedNodes = baseNodes;
    for (const [name, spec] of this.customNodes) {
      extendedNodes = extendedNodes.addToEnd(name, spec);
      log.info(`[SchemaExtension] Added node to schema: ${name}`);
    }
    
    // Build new schema
    const extendedSchema = new Schema({
      nodes: extendedNodes,
      marks: extendedMarks
    });
    
    log.info(`[SchemaExtension] Built extended schema with ${this.customMarks.size} custom marks and ${this.customNodes.size} custom nodes`);
    
    return extendedSchema;
  }
  
  /**
   * Reset registry (for testing or hot reload)
   */
  reset(): void {
    this.customMarks.clear();
    this.customNodes.clear();
    this.isLocked = false;
    log.info('[SchemaExtension] Registry reset');
  }
  
  /**
   * Get all registered marks
   */
  getRegisteredMarks(): string[] {
    return Array.from(this.customMarks.keys());
  }
  
  /**
   * Get all registered nodes
   */
  getRegisteredNodes(): string[] {
    return Array.from(this.customNodes.keys());
  }
}

export const schemaExtensionRegistry = new SchemaExtensionRegistry();


